"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { wholeWordSource } from "@/lib/tool-kit/text/whole-word";

interface ReplacePair {
  id: number;
  find: string;
  replace: string;
}

interface PairResult {
  id: number;
  find: string;
  replace: string;
  count: number;
}

interface RunResult {
  output: string;
  pairResults: PairResult[];
  total: number;
}

const MAX_PAIRS = 20;

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

function escapeRegExp(value: string): string {
  return value.replace(REGEX_SPECIAL_CHARS, "\\$&");
}

export function applyPairs(
  text: string,
  pairs: ReplacePair[],
  options: { caseSensitive: boolean; wholeWord: boolean }
): RunResult {
  let current = text;
  const pairResults: PairResult[] = [];
  let total = 0;
  for (const pair of pairs) {
    if (pair.find.length === 0) {
      continue;
    }
    const escaped = escapeRegExp(pair.find);
    const source = options.wholeWord ? wholeWordSource(escaped) : escaped;
    const flags = `${options.caseSensitive ? "g" : "gi"}${options.wholeWord ? "u" : ""}`;
    const pattern = new RegExp(source, flags);
    let count = 0;
    current = current.replace(pattern, () => {
      count += 1;
      return pair.replace;
    });
    total += count;
    pairResults.push({
      id: pair.id,
      find: pair.find,
      replace: pair.replace,
      count,
    });
  }
  return { output: current, pairResults, total };
}

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_REPLACER_STRINGS = {
  pairsLabel: "Find → replace pairs (applied in order, top to bottom)",
  findAriaLabel: "Find text for pair {n}",
  findPlaceholder: "Find",
  replaceAriaLabel: "Replacement text for pair {n}",
  replacePlaceholder: "Replace with",
  removeAriaLabel: "Remove pair {n}",
  addPair: "+ Add pair ({count}/{max})",
  pairsOrderHint:
    "Pairs apply in order — an earlier replacement can affect what a later pair finds.",
  caseSensitive: "Case-sensitive",
  wholeWordOnly: "Whole word only",
  inputText: "Input text",
  inputPlaceholder: "Paste the text you want to run the replacements on",
  replaceAll: "Replace All",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  output: "Output",
  tableHeaderIndex: "#",
  tableHeaderFind: "Find",
  tableHeaderReplace: "Replace with",
  tableHeaderReplacements: "Replacements",
  total: "Total",
} as const;

export default function WordReplacerWidget() {
  const strings = useToolUi(WORD_REPLACER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [pairs, setPairs] = useState<ReplacePair[]>([
    { id: 1, find: "", replace: "" },
  ]);
  const [nextId, setNextId] = useState(2);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [copied, setCopied] = useState(false);

  const hasFindablePair = pairs.some((p) => p.find.length > 0);

  function addPair() {
    if (pairs.length >= MAX_PAIRS) {
      return;
    }
    setPairs([...pairs, { id: nextId, find: "", replace: "" }]);
    setNextId(nextId + 1);
  }

  function removePair(id: number) {
    if (pairs.length <= 1) {
      return;
    }
    setPairs(pairs.filter((p) => p.id !== id));
  }

  function updatePair(id: number, field: "find" | "replace", value: string) {
    setPairs(pairs.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  function handleReplace() {
    if (input.length === 0 || !hasFindablePair) {
      return;
    }
    setResult(applyPairs(input, pairs, { caseSensitive, wholeWord }));
    setCopied(false);
  }

  function handleCopy() {
    if (!result || result.output.length === 0) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setResult(null);
    setCopied(false);
  }

  return (
    <>
      {/* Pairs */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.pairsLabel}
        </p>
        <div className="space-y-2">
          {pairs.map((pair, index) => (
            <div className="flex flex-wrap items-center gap-2" key={pair.id}>
              <span className="w-6 text-muted-foreground text-xs">
                {index + 1}.
              </span>
              <input
                aria-label={strings.findAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="min-w-0 flex-1 rounded-lg border border-border bg-card p-2 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => updatePair(pair.id, "find", e.target.value)}
                placeholder={strings.findPlaceholder}
                type="text"
                value={pair.find}
              />
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
              <input
                aria-label={strings.replaceAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="min-w-0 flex-1 rounded-lg border border-border bg-card p-2 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => updatePair(pair.id, "replace", e.target.value)}
                placeholder={strings.replacePlaceholder}
                type="text"
                value={pair.replace}
              />
              <button
                aria-label={strings.removeAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={pairs.length <= 1}
                onClick={() => removePair(pair.id)}
                type="button"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={pairs.length >= MAX_PAIRS}
          onClick={addPair}
          type="button"
        >
          {strings.addPair
            .replace("{count}", String(pairs.length))
            .replace("{max}", String(MAX_PAIRS))}
        </button>
        <p className="text-muted-foreground text-xs">
          {strings.pairsOrderHint}
        </p>
      </section>

      {/* Options */}
      <section className="flex flex-wrap gap-3">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <input
            checked={caseSensitive}
            className="accent-primary"
            onChange={(e) => setCaseSensitive(e.target.checked)}
            type="checkbox"
          />
          {strings.caseSensitive}
        </label>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <input
            checked={wholeWord}
            className="accent-primary"
            onChange={(e) => setWholeWord(e.target.checked)}
            type="checkbox"
          />
          {strings.wholeWordOnly}
        </label>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="replace-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="replace-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0 || !hasFindablePair}
          onClick={handleReplace}
          type="button"
        >
          {strings.replaceAll}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!result}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && !result}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Results */}
      {result && (
        <>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="replace-output"
            >
              {strings.output}
            </label>
            <textarea
              className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
              id="replace-output"
              readOnly
              value={result.output}
            />
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b bg-muted/30">
                  <th className="p-3 font-medium text-foreground">
                    {strings.tableHeaderIndex}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.tableHeaderFind}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.tableHeaderReplace}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.tableHeaderReplacements}
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.pairResults.map((pr, index) => (
                  <tr className="border-border border-b" key={pr.id}>
                    <td className="p-3 text-muted-foreground">{index + 1}</td>
                    <td className="p-3 font-mono text-foreground">{pr.find}</td>
                    <td className="p-3 font-mono text-foreground">
                      {pr.replace}
                    </td>
                    <td className="p-3 text-foreground">{fmt(pr.count)}</td>
                  </tr>
                ))}
                <tr className="bg-muted/30">
                  <td className="p-3 font-medium text-foreground" colSpan={3}>
                    {strings.total}
                  </td>
                  <td className="p-3 font-medium text-foreground">
                    {fmt(result.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
